import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { Dialog, DialogContentHeadless } from '../../design-system/Dialog';
import { CreateCallsheetDialogContent } from './CreateCallsheetDialogContent';
import { MountElementProps } from '../../messages/mount-element';
import type { CallsheetFormData } from './types';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithServices } from 'v5/platform/ts/services';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';

export interface CreateCallsheetDialogProps
  extends WithServices<
    [
      WithProductionService,
      WithProductionTypeService,
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithUserPreferencesService,
      WithAppScopeService,
      WithEnterpriseService,
      WithFeatureService
    ]
  > {
  onSubmit?: (data: CallsheetFormData) => Promise<void> | void;
  preselectedProductionIds?: number[];
}

/**
 * Create Callsheet Dialog - v5 implementation
 *
 * Pure dialog component without trigger. Designed to be used with MountElementHandler.
 * Wraps the callsheet creation dialog with unsaved changes protection.
 *
 * Usage via UserFlowService:
 * ```
 * myServices.userFlowService.openCreateCallsheetDialog();
 * ```
 */
export function CreateCallsheetDialog({
  open,
  onOpenChange,
  onSubmit,
  preselectedProductionIds,
  services
}: CreateCallsheetDialogProps & MountElementProps) {
  return (
    <UnsavedChangesProtection
      open={open}
      onOpenChange={onOpenChange}
      beforeUnloadMessage="You have unsaved changes in the callsheet form!"
    >
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless>
            <CreateCallsheetDialogContent
              open={dialogProps.open}
              onSubmit={onSubmit}
              preselectedProductionIds={preselectedProductionIds}
              services={services}
            />
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}
